/**
 * Kid Donation Modal
 * Extends the base modal for kid dedication donations
 */

import AccessibleModal from './modal.js';
import { devLog } from './utils.js';

class KidDonationModal extends AccessibleModal {
    constructor(options = {}) {
        const modalOptions = {
            closeOnBackdrop: false, // Keep open for form interaction
            focusOnOpen: true,
            ...options
        };

        super('kid-donation-modal', modalOptions);
        
        this.selectedAmount = null;
        this.customAmount = '';
        this.formData = {};
        
        // Bind methods
        this.handleAmountSelection = this.handleAmountSelection.bind(this);
        this.handleCustomAmountChange = this.handleCustomAmountChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        
        devLog('Kid Donation Modal initialized');
    }

    openForKid(kidData) {
        if (!kidData || !kidData.name) {
            console.error('Kid data is required to open donation modal');
            return;
        }

        this.kidData = kidData;
        const content = this.generateDonationForm(kidData);
        const title = `Dedicate a Donation to ${kidData.name}`;
        
        this.open(content, title);
        this.attachFormEventListeners();
    }

    generateDonationForm(kidData) {
        const amounts = [1000, 500, 250, 125, 60]; // Preset amounts
        const customAmountValue = this.customAmount || '60.00';

        return `
            <form class="donation-form" data-kid-id="${kidData.id}">
                <div class="donation-form__amounts">
                    <input type="text" 
                           class="donation-form__custom-amount" 
                           placeholder="$60.00" 
                           value="$${customAmountValue}"
                           data-amount="custom">
                    
                    <div class="donation-form__preset-amounts">
                        ${amounts.map(amount => `
                            <button type="button" 
                                    class="donation-form__amount-button" 
                                    data-amount="${amount}">
                                $${amount.toLocaleString()}
                            </button>
                        `).join('')}
                        <button type="button" 
                                class="donation-form__amount-button donation-form__amount-button--custom" 
                                data-amount="custom">
                            Custom<br>Amount
                        </button>
                    </div>
                </div>

                <div class="donation-form__recurring">
                    <label class="donation-form__checkbox-label">
                        <input type="checkbox" name="recurring" value="monthly" class="donation-form__checkbox">
                        Make this donation every month
                    </label>
                </div>

                <div class="donation-form__dedication">
                    <h3 class="donation-form__section-title">Dedicate this Donation</h3>
                    
                    <div class="donation-form__radio-group">
                        <label class="donation-form__radio-label">
                            <input type="radio" name="dedication" value="yes" checked class="donation-form__radio">
                            Yes, please
                        </label>
                        <label class="donation-form__radio-label">
                            <input type="radio" name="dedication" value="no" class="donation-form__radio">
                            No, thank you
                        </label>
                    </div>

                    <div class="donation-form__dedication-fields" id="dedication-fields">
                        <div class="donation-form__field form-group">
                            <label for="dedication-type" class="donation-form__label form-label">Dedication Type</label>
                            <select id="dedication-type" name="dedication_type" class="donation-form__select form-select">
                                <option value="in_honor_of">in honor of</option>
                                <option value="in_memory_of">in memory of</option>
                                <option value="in_celebration_of">in celebration of</option>
                            </select>
                        </div>

                        <div class="donation-form__field form-group">
                            <label for="honoree-name" class="donation-form__label form-label">Honoree Name</label>
                            <input type="text" 
                                   id="honoree-name" 
                                   name="honoree_name" 
                                   value="${kidData.name}" 
                                   class="donation-form__input form-input"
                                   readonly>
                        </div>

                        <div class="donation-form__honoree-preview">
                            ${kidData.image ? `
                                <div class="donation-form__honoree-image">
                                    <img src="${kidData.image}" alt="${kidData.name}" class="donation-form__honoree-img">
                                </div>
                            ` : ''}
                            <div class="donation-form__honoree-info">
                                <h4 class="donation-form__honoree-name">${kidData.name}</h4>
                            </div>
                        </div>

                        <div class="donation-form__field">
                            <fieldset class="donation-form__fieldset">
                                <legend class="donation-form__legend">Would you like us to notify anyone of your donation?</legend>
                                <label class="donation-form__radio-label">
                                    <input type="radio" name="notification" value="mail_card" class="donation-form__radio">
                                    Yes, mail a card
                                </label>
                                <label class="donation-form__radio-label">
                                    <input type="radio" name="notification" value="send_ecard" class="donation-form__radio">
                                    Yes, send an eCard
                                </label>
                                <label class="donation-form__radio-label">
                                    <input type="radio" name="notification" value="no_thanks" checked class="donation-form__radio">
                                    No thanks
                                </label>
                            </fieldset>
                        </div>
                    </div>
                </div>

                <div class="donation-form__payment">
                    <h3 class="donation-form__section-title">Select Payment Method</h3>
                    <div class="donation-form__payment-methods">
                        <label class="donation-form__payment-method">
                            <input type="radio" name="payment_method" value="credit_card" checked class="donation-form__radio">
                            <span class="donation-form__payment-label">Credit Card</span>
                        </label>
                        <!-- Future payment methods can be added here -->
                    </div>
                </div>

                <div class="donation-form__actions">
                    <button type="button" class="prop-button prop-button--outline-blue" data-modal-close>
                        Cancel
                    </button>
                    <button type="submit" class="prop-button prop-button--solid-orange">
                        Continue to Payment
                    </button>
                </div>

                <input type="hidden" name="kid_id" value="${kidData.id}">
                <input type="hidden" name="kid_slug" value="${kidData.slug || ''}">
            </form>
        `;
    }

    attachFormEventListeners() {
        if (!this.modal) return;

        // Amount selection
        const amountButtons = this.modal.querySelectorAll('.donation-form__amount-button');
        amountButtons.forEach(button => {
            button.addEventListener('click', this.handleAmountSelection);
        });

        // Custom amount input
        const customAmountInput = this.modal.querySelector('.donation-form__custom-amount');
        if (customAmountInput) {
            customAmountInput.addEventListener('input', this.handleCustomAmountChange);
            customAmountInput.addEventListener('focus', () => {
                this.selectAmount('custom');
            });
        }

        // Dedication toggle
        const dedicationRadios = this.modal.querySelectorAll('input[name="dedication"]');
        dedicationRadios.forEach(radio => {
            radio.addEventListener('change', (e) => {
                const dedicationFields = this.modal.querySelector('#dedication-fields');
                if (dedicationFields) {
                    dedicationFields.style.display = e.target.value === 'yes' ? 'block' : 'none';
                }
            });
        });

        // Form submission
        const form = this.modal.querySelector('.donation-form');
        if (form) {
            form.addEventListener('submit', this.handleFormSubmit);
        }
    }

    handleAmountSelection(e) {
        e.preventDefault();
        const amount = e.target.dataset.amount;
        this.selectAmount(amount);
    }

    selectAmount(amount) {
        // Remove previous selection
        const buttons = this.modal.querySelectorAll('.donation-form__amount-button');
        buttons.forEach(btn => btn.classList.remove('donation-form__amount-button--selected'));

        if (amount === 'custom') {
            const customButton = this.modal.querySelector('[data-amount="custom"]');
            const customInput = this.modal.querySelector('.donation-form__custom-amount');
            
            if (customButton) customButton.classList.add('donation-form__amount-button--selected');
            if (customInput) customInput.focus();
            
            this.selectedAmount = 'custom';
        } else {
            const selectedButton = this.modal.querySelector(`[data-amount="${amount}"]`);
            if (selectedButton && !selectedButton.classList.contains('donation-form__amount-button--custom')) {
                selectedButton.classList.add('donation-form__amount-button--selected');
                this.selectedAmount = parseInt(amount);
            }
        }

        devLog('Amount selected:', this.selectedAmount);
    }

    handleCustomAmountChange(e) {
        let value = e.target.value;
        
        // Remove non-numeric characters except decimal point
        value = value.replace(/[^0-9.]/g, '');
        
        // Ensure only one decimal point
        const parts = value.split('.');
        if (parts.length > 2) {
            value = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Limit to 2 decimal places
        if (parts[1] && parts[1].length > 2) {
            value = parts[0] + '.' + parts[1].substring(0, 2);
        }
        
        // Add dollar sign
        if (value && !value.startsWith('$')) {
            value = '$' + value;
        }
        
        e.target.value = value;
        this.customAmount = value.replace('$', '');
        
        // Auto-select custom amount
        this.selectAmount('custom');
    }

    handleFormSubmit(e) {
        e.preventDefault();
        
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        
        // Add amount information
        if (this.selectedAmount === 'custom') {
            data.amount = this.customAmount;
        } else {
            data.amount = this.selectedAmount;
        }
        
        // Add kid information
        data.kid_name = this.kidData.name;
        
        devLog('Donation form data:', data);
        
        // TODO: Integrate with GiveWP or payment processor
        this.processDonation(data);
    }

    processDonation(data) {
        // For now, just show a success message
        // Later this will integrate with GiveWP or direct payment processing
        
        const successContent = `
            <div class="donation-success">
                <div class="donation-success__icon">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="24" fill="#10b981"/>
                        <path d="M18 24l6 6 12-12" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h3 class="donation-success__title">Form Ready!</h3>
                <p class="donation-success__message">
                    Your donation form for ${this.kidData.name} is configured. 
                    When GiveWP is enabled, this will process the $${data.amount} donation.
                </p>
                <div class="donation-success__details">
                    <strong>Details:</strong><br>
                    Amount: $${data.amount}<br>
                    Honoree: ${data.kid_name}<br>
                    Recurring: ${data.recurring ? 'Yes' : 'No'}
                </div>
            </div>
        `;
        
        this.setTitle('Donation Configuration Complete');
        this.setContent(successContent);
        
        // Auto-close after a few seconds
        setTimeout(() => {
            this.close();
        }, 5000);
    }

    // Override the open method to add donation-specific classes
    open(content, title, footerContent) {
        super.open(content, title, footerContent);
        this.modal.classList.add('modal--donation');
    }
}

export default KidDonationModal;
